import { useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import { isLoading } from '#src/app/shared/utils/async';

import { useGeolocationValues } from '../signForm/useGeolocationValues';
import { useSingleAddressFieldForUnauthenticated } from '../signForm/useSingleAddressFieldForUnauthenticated';

type AddressRemovalExperiment = PetitionDetailsPageUserData['experiments']['signFormAddressRemovalExperiment'];

/**
 * Passed to useTrackExperimentExposure (which must be called unconditionally, per Rules of Hooks) while
 * the visitor is unqualified — `treated: false` keeps it a no-op.
 */
const INERT_EXPERIMENT = {
	type: 'experiment-service',
	experimentName: 'sign_form_address_removal_experiment',
	variation: 'control',
	config: {},
	treated: false,
} as AddressRemovalExperiment;

/**
 * Treats `sign_form_address_removal_experiment` and fires `$exposure` for a visitor who would be shown
 * the IP-derived address ask. Fires for every arm — control and fake_control included, since `qualified`
 * is computed identically in all three.
 *
 * Mount this exactly once, from a location that renders in every arm. It must NOT go inside the sign
 * form: the desktop sidebar and the mobile sign modal each mount their own, and exposure dedup is per
 * mount, so a second instance would halve every measured rate.
 */
export function useTreatSignFormAddressRemovalExperiment(): void {
	const utilityContext = useUtilityContext();
	const {
		data: {
			petition: { id: petitionId, status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: {
			decisionMaker,
			experiments: { signFormAddressRemovalExperiment: experiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: { enabled: singleAddressField },
	} = useSingleAddressFieldForUnauthenticated();
	const geolocValues = useGeolocationValues();

	const [treated, setTreated] = useState(false);
	// Ref, not state: `treated` only flips a tick later, and SPA navigation reuses this component without
	// remounting. Holding the petition rather than a boolean lets the next petition treat.
	const treatedPetitionRef = useRef<string | null>(null);

	/**
	 * Every sign-form entry point — `Side` and `MobileActionButton` — refuses to render on a closed or
	 * victorious petition, and to a decision maker. `LocalizationFields` never mounts for those visitors in
	 * any arm, so treating them would only pad the denominator. No `signed` check: `getPetitionViewerInfo`
	 * hard-codes it false without a `currentUserId`, and the normalizer already restricts this to `GUEST`.
	 */
	const signFormReachable = !decisionMaker && status !== 'CLOSED' && status !== 'VICTORY';

	/**
	 * The same condition that makes `LocalizationFields` render `GeolocLocalizationFields`: the single
	 * address field wins when enabled, and `geolocPreview`'s initial value is `!!geolocValues.values`.
	 * Derived from the same hook the form uses, never re-implemented — `geolocValues.values` only exists
	 * when geoip yielded a complete, country-valid address, which is also what stops `v1` from hiding a
	 * form that can't be submitted.
	 *
	 * Only the unauthenticated half of `useAddressOptions` is checked: its authenticated half requires
	 * `AUTHENTICATED`, which the normalizer has already excluded, and it lives inside the sign-form
	 * container where a shared hook shouldn't reach.
	 */
	const qualified = signFormReachable && !singleAddressField && !isLoading(geolocValues) && !!geolocValues.values;
	const isRealAssignment = !experiment.fallback && !experiment.override;

	useEffect(() => {
		if (!qualified || !isRealAssignment || treatedPetitionRef.current === petitionId) return;
		treatedPetitionRef.current = petitionId;
		(async () => {
			// treatExperiment swallows its own errors and always resolves, so a silent server-side failure is
			// indistinguishable from success — the same tradeoff every treat-on-X caller accepts.
			await treatExperiment(utilityContext, experiment.experimentName);
			setTreated(true);
		})().catch((e: unknown) => {
			// Backstop if treatExperiment's resolve-never-reject contract ever regresses — this must not be
			// silent, since it would drop $exposure for the whole experiment.
			void utilityContext.errorReporter.report({
				error:
					e instanceof Error
						? e
						: new Error('useTreatSignFormAddressRemovalExperiment: unexpected rejection from treatExperiment'),
				params: { experimentName: experiment.experimentName },
			});
		});
	}, [qualified, isRealAssignment, petitionId, experiment.experimentName, utilityContext]);

	/**
	 * `treatExperiment` registers treatment server-side but the resolved object stays `treated: false`, so
	 * it has to be flipped locally or `useTrackExperimentExposure` no-ops. `|| override` is needed because
	 * that hook returns on `!treated` before reaching its `override` branch, and a `treat: false` field
	 * resolves an override with `treated: false` on a fresh session — without it a QA `?exo.` visit gets no
	 * "exposure would have fired" log. No real exposure leaks: that branch logs and returns before sending.
	 *
	 * Unqualified visitors get INERT_EXPERIMENT rather than the real one. `treated` would otherwise pick up
	 * `experiment.treated`/`override` from a visitor who never reaches the sign form, exposing them for
	 * real the moment the normalizer stops forcing `treated: false` — and today it already hands QA a
	 * misleading "$exposure SUPPRESSED by override" log for a visitor who was never going to be exposed.
	 */
	const experimentForExposure = useMemo<AddressRemovalExperiment>(() => {
		if (!qualified) return INERT_EXPERIMENT;
		const nowTreated = treated || experiment.treated || Boolean(experiment.override);
		return nowTreated ? { ...experiment, treated: true } : experiment;
	}, [qualified, experiment, treated]);
	// `resetKeys` is required, not optional: variations are deterministic per user, so without the petition
	// id an SPA petition-to-petition navigation would dedup the new petition's legitimate exposure.
	const trackExperimentExposure = useTrackExperimentExposure(experimentForExposure, { resetKeys: [petitionId] });

	useEffect(() => {
		if (!experimentForExposure.treated) return;
		void trackExperimentExposure();
	}, [experimentForExposure.treated, trackExperimentExposure]);
}
